import { useEffect, useState } from "react";
import { Filter, Action, Area } from "./types";
import { AreaCard } from "./components/AreaCard";
import { PriorityChart } from "./components/PriorityChart";
import { ConceptsSection } from "./components/ConceptsSection";
import { Download, Filter as FilterIcon, LogOut, Search } from "lucide-react";
import PriorityOption from "./types/Priority";
import { ItemPriority } from "./types/ItemPriority";
import formatPriority from "./utils/formatPriority";
import { useLogout } from "./hooks/Auth/useLogout";
import { getAllAreasWithActions } from "./services/admin/areas";
import isFutureDate from "./utils/isFutureDate";
import { Modal } from "./components/Modal";
import { useGlossaryStore } from "./stores/glossary.store";
import AppLoading from "./components/AppLoading";

export default function App() {
	const [filter, setFilter] = useState<Filter>("all");
	const [search, setSearch] = useState("");
	const [dados, setDados] = useState<Area[]>([]);
	const logout = useLogout();
	const [selectedAction, setSelectedAction] = useState<Action | null>(null);

	const handleActionClick = (action: Action) => {
		setSelectedAction(action);
		window.history.pushState({}, "", `/acoes/${action.slug}`);
	};

	const handleCloseModal = () => {
		setSelectedAction(null);
		window.history.back();
	};

	const { loading, fetchGlossary } = useGlossaryStore();
	const [isloading, setLoading] = useState(true);

	const [priority, setPriority] = useState<PriorityOption[]>([
		{ value: "all", label: "Todas" },
	]);

	useEffect(() => {
		fetchGlossary();

		const priorityMap: Record<ItemPriority["priority"], string> = {
			high: "Alta",
			medium: "Média",
			low: "Baixa",
			loading: "Calculando Prioridade",
		};

		const desiredOrder: ItemPriority["priority"][] = [
			"high",
			"medium",
			"low",
			"loading",
		];

		const getAreas = async () => {
			try {
				const data = await getAllAreasWithActions();

				setDados(data);

				const prioritySet = new Set<keyof typeof priorityMap>(
					data.flatMap((area: Area) =>
						area.actions
							.map((action: ItemPriority) => action.priority)
							.filter((p): p is keyof typeof priorityMap => p in priorityMap)
					)
				);

				const priorityFormatted: PriorityOption[] = desiredOrder
					.filter((p): p is keyof typeof priorityMap => prioritySet.has(p))
					.map((p) => ({
						value: p,
						label: priorityMap[p],
					}));

				setPriority([{ value: "all", label: "Todas" }, ...priorityFormatted]);
			} catch (err) {
				console.error(err);
			} finally {
				setLoading(false);
			}
		};

		getAreas();
	}, [getAllAreasWithActions]);

	const handleExport = () => {
		const data = dados
			.filter((area) => {
				return !isFutureDate(area.shippedAt);
			})
			.map((area) => ({
				área: area.title,
				prioridade_geral: formatPriority(area.priority),
				ações: area.actions.map((action: Action) => ({
					descrição: action.title,
					prioridade: formatPriority(action.priority),
					problema: action.problem_html,
					plano: action.plan,
					critérios: action.checklist.map((item) => ({
						título: item.checklistTitle,
						descrição: item.checklistDescription_html,
					})),
					resolvido: action.resolved === "1" ? "Sim" : "Não",
				})),
			}));

		const blob = new Blob([JSON.stringify(data, null, 2)], {
			type: "application/json",
		});

		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");

		a.href = url;
		a.download = "auditoria-tech-360.json";

		document.body.appendChild(a);

		a.click();
		document.body.removeChild(a);

		URL.revokeObjectURL(url);
	};

	const filteredAreas = dados.map((area) => ({
		...area,
		actions: area.actions.filter(
			(action: Action) =>
				action.title.toLowerCase().includes(search.toLowerCase()) ||
				action.problem_html.toLowerCase().includes(search.toLowerCase())
		),
	}));

	if (loading || isloading) return <AppLoading />;

	return (
		<div className="min-h-screen bg-gray-900">
			<div className="max-w-7xl mx-auto px-4 py-8">
				<div className="flex items-center justify-between mb-8">
					<div className="flex items-center gap-6">
						<img
							src="https://stoix.dev/images/logo.svg"
							alt="Stoix"
							className="h-8"
						/>
						<h1 className="text-2xl font-bold text-white">
							Discovery 360º - Status e Ações Prioritárias
						</h1>
					</div>
					<div className="flex items-center gap-2">
						<button
							onClick={handleExport}
							className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
						>
							<Download size={20} />
							Exportar
						</button>
						<button
							onClick={logout}
							className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
						>
							<LogOut size={20} />
							Sair
						</button>
					</div>
				</div>
				<div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
					<div className="flex items-center gap-4">
						<FilterIcon size={20} className="text-gray-400" />
						<div className="flex gap-2">
							{priority.map((option) => (
								<button
									key={option.value}
									onClick={() => setFilter(option.value as Filter)}
									className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
										filter === option.value
											? "bg-blue-600 text-white"
											: "bg-gray-800 text-gray-300 hover:bg-gray-700"
									}`}
								>
									{option.label}
								</button>
							))}
						</div>
					</div>
					<div className="relative flex-1 min-w-[300px]">
						<Search
							size={20}
							className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
						/>
						<input
							type="text"
							placeholder="Buscar ações..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
						/>
					</div>
				</div>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
					{filteredAreas.map((area) => (
						<AreaCard
							key={area.id}
							area={area}
							filter={filter}
							onActionClick={handleActionClick}
						/>
					))}
				</div>
				<PriorityChart areas={dados} />
				<ConceptsSection />

				{selectedAction && (
					<Modal
						action={selectedAction}
						isOpen={!!selectedAction}
						onClose={handleCloseModal}
					/>
				)}
			</div>
		</div>
	);
}
